/**
 * The account's dictation vocabulary, mirrored across desktop windows. Any
 * window may change it: the API call runs in that window and the result is
 * set here, which reaches the owner, the one window that pushes the words to
 * Rust (`use-dictation-vocabulary-lifecycle`). Changes are optimistic and
 * roll back only the entry they touched; suggestions are refetched after any
 * change that can add or free a name.
 */
import { create } from "zustand";
import {
  addHeardAs as postHeardAs,
  addVocabularyWord,
  dismissVocabularySuggestion,
  learnVocabulary,
  listVocabulary,
  listVocabularySuggestions,
  removeHeardAs as deleteHeardAs,
  removeVocabularyWord,
  renameVocabularyWord,
  type VocabularySuggestion,
  type VocabularyWord,
} from "@/api/voice-vocabulary";
import {
  trackVocabularyWordAdded,
  trackVocabularyWordLearned,
} from "@/desktop/dictation-vocabulary-analytics";
import { classifyVoiceText } from "@/desktop/dictation-vocabulary-native";
import { mirroredStore } from "@/lib/mirrored-store";
import { useDictationSettingsStore } from "./dictation-settings-store";

/** Ids of entries shown before the server answered. */
const PENDING_PREFIX = "pending:";
let pendingCount = 0;
const pendingId = () => `${PENDING_PREFIX}${++pendingCount}`;

export const isPendingVocabularyId = (id: string) =>
  id.startsWith(PENDING_PREFIX);

export type VocabularyLearnInput = {
  word: string;
  heard: string;
  left: string;
  right: string;
  surface: "recent_dictation" | "composer";
};

type DictationVocabularyState = {
  /** Most recently changed first, as the server orders them. */
  words: VocabularyWord[];
  /** At most three Memory names that are not in the list yet. */
  suggestions: VocabularySuggestion[];
  /** When the list was last read from the server; null before that. */
  loadedAt: number | null;
  load: () => Promise<void>;
  addWord: (text: string, source: "user" | "memory") => Promise<VocabularyWord>;
  /** Rejects with the API error (409 when the text is taken) after reverting. */
  renameWord: (wordId: string, text: string) => Promise<VocabularyWord>;
  removeWord: (wordId: string) => Promise<void>;
  addHeardAs: (wordId: string, text: string) => Promise<VocabularyWord>;
  removeHeardAs: (wordId: string, heardId: string) => Promise<void>;
  addSuggestion: (name: string) => Promise<VocabularyWord>;
  dismissSuggestion: (name: string) => Promise<void>;
  learn: (input: VocabularyLearnInput) => Promise<VocabularyWord>;
  clear: () => void;
};

const sameText = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

const classify = (text: string) =>
  classifyVoiceText(text, useDictationSettingsStore.getState().locale);

/** Puts the server's word first, replacing the entry it supersedes and
 *  taking its mishearings from any other word (a mishearing has one word). */
function upsert(
  words: VocabularyWord[],
  word: VocabularyWord,
  supersedes?: string,
): VocabularyWord[] {
  const owned = new Set(word.heard_as.map((heard) => heard.text.toLowerCase()));
  const others = words
    .filter((entry) => entry.id !== word.id && entry.id !== supersedes)
    .map((entry) =>
      entry.heard_as.some((heard) => owned.has(heard.text.toLowerCase()))
        ? {
            ...entry,
            heard_as: entry.heard_as.filter(
              (heard) => !owned.has(heard.text.toLowerCase()),
            ),
          }
        : entry,
    );
  return [word, ...others];
}

function mapWord(
  words: VocabularyWord[],
  wordId: string,
  change: (word: VocabularyWord) => VocabularyWord,
): VocabularyWord[] {
  return words.map((word) => (word.id === wordId ? change(word) : word));
}

export const useDictationVocabularyStore = create<DictationVocabularyState>()(
  mirroredStore("dictationVocabulary", (set, get) => {
    // Bumped by clear (sign-out, account switch): an answer that arrives
    // for the account before is dropped, success and rollback alike.
    let account = 0;
    // Bumped by every load: only the newest load is applied.
    let loadSeq = 0;
    /** A `set` that writes only while the account it started under is current. */
    const forThisAccount = () => {
      const started = account;
      return (
        update:
          | Partial<DictationVocabularyState>
          | ((
              state: DictationVocabularyState,
            ) => Partial<DictationVocabularyState>),
      ) => {
        if (started === account) set(update);
      };
    };
    const refreshSuggestions = () => {
      const write = forThisAccount();
      return listVocabularySuggestions().then(
        (suggestions) => write({ suggestions }),
        () => undefined,
      );
    };

    return {
      words: [],
      suggestions: [],
      loadedAt: null,

      load: async () => {
        const write = forThisAccount();
        const current = ++loadSeq;
        const [words, suggestions] = await Promise.all([
          listVocabulary(),
          listVocabularySuggestions().catch(() => get().suggestions),
        ]);
        if (current === loadSeq) {
          write({ words, suggestions, loadedAt: Date.now() });
        }
      },

      addWord: async (text, source) => {
        const write = forThisAccount();
        const id = pendingId();
        set((state) => ({
          words: [
            {
              id,
              text,
              real_word: true,
              source,
              heard_as: [],
              updated_at: new Date().toISOString(),
            },
            ...state.words,
          ],
          suggestions: state.suggestions.filter(
            (suggestion) => !sameText(suggestion.name, text),
          ),
        }));
        try {
          const realWord = await classify(text);
          const { word, created } = await addVocabularyWord({
            text,
            real_word: realWord,
            source,
          });
          write((state) => ({ words: upsert(state.words, word, id) }));
          if (created) {
            trackVocabularyWordAdded(
              source === "memory" ? "memory" : "settings",
              text,
            );
          }
          return word;
        } catch (error) {
          write((state) => ({
            words: state.words.filter((word) => word.id !== id),
          }));
          throw error;
        } finally {
          void refreshSuggestions();
        }
      },

      renameWord: async (wordId, text) => {
        const before = get().words.find((word) => word.id === wordId);
        if (!before) throw new Error("Unknown vocabulary word");
        const write = forThisAccount();
        set((state) => ({
          words: mapWord(state.words, wordId, (word) => ({ ...word, text })),
        }));
        try {
          const realWord = await classify(text);
          const word = await renameVocabularyWord(wordId, {
            text,
            real_word: realWord,
          });
          write((state) => ({ words: upsert(state.words, word) }));
          return word;
        } catch (error) {
          write((state) => ({
            words: mapWord(state.words, wordId, (word) => ({
              ...word,
              text: before.text,
            })),
          }));
          throw error;
        }
      },

      removeWord: async (wordId) => {
        const words = get().words;
        const index = words.findIndex((word) => word.id === wordId);
        const removed = words[index];
        if (!removed) return;
        const write = forThisAccount();
        set((state) => ({
          words: state.words.filter((word) => word.id !== wordId),
        }));
        try {
          await removeVocabularyWord(wordId);
        } catch (error) {
          write((state) => {
            const next = [...state.words];
            next.splice(Math.min(index, next.length), 0, removed);
            return { words: next };
          });
          throw error;
        } finally {
          void refreshSuggestions();
        }
      },

      addHeardAs: async (wordId, text) => {
        const write = forThisAccount();
        const id = pendingId();
        set((state) => ({
          words: mapWord(state.words, wordId, (word) => ({
            ...word,
            heard_as: [
              ...word.heard_as,
              { id, text, real_word: true, contexts: [], source: "user" },
            ],
          })),
        }));
        try {
          const realWord = await classify(text);
          const word = await postHeardAs(wordId, { text, real_word: realWord });
          write((state) => ({ words: upsert(state.words, word) }));
          return word;
        } catch (error) {
          write((state) => ({
            words: mapWord(state.words, wordId, (word) => ({
              ...word,
              heard_as: word.heard_as.filter((heard) => heard.id !== id),
            })),
          }));
          throw error;
        }
      },

      removeHeardAs: async (wordId, heardId) => {
        const word = get().words.find((entry) => entry.id === wordId);
        const index = word?.heard_as.findIndex((heard) => heard.id === heardId);
        const removed = index === undefined ? undefined : word?.heard_as[index];
        if (!removed || index === undefined) return;
        const write = forThisAccount();
        set((state) => ({
          words: mapWord(state.words, wordId, (entry) => ({
            ...entry,
            heard_as: entry.heard_as.filter((heard) => heard.id !== heardId),
          })),
        }));
        try {
          const updated = await deleteHeardAs(wordId, heardId);
          write((state) => ({ words: upsert(state.words, updated) }));
        } catch (error) {
          write((state) => ({
            words: mapWord(state.words, wordId, (entry) => {
              const heardAs = [...entry.heard_as];
              heardAs.splice(Math.min(index, heardAs.length), 0, removed);
              return { ...entry, heard_as: heardAs };
            }),
          }));
          throw error;
        }
      },

      addSuggestion: (name) => get().addWord(name, "memory"),

      dismissSuggestion: async (name) => {
        const before = get().suggestions;
        const write = forThisAccount();
        set({
          suggestions: before.filter((suggestion) => suggestion.name !== name),
        });
        try {
          await dismissVocabularySuggestion(name);
        } catch (error) {
          write({ suggestions: before });
          throw error;
        }
        void refreshSuggestions();
      },

      learn: async ({ word: text, heard, left, right, surface }) => {
        const write = forThisAccount();
        const newWord = !get().words.some((word) => sameText(word.text, text));
        const [wordReal, heardReal] = await Promise.all([
          classify(text),
          classify(heard),
        ]);
        const word = await learnVocabulary({
          word: text,
          word_real: wordReal,
          heard,
          heard_real: heardReal,
          left,
          right,
        });
        write((state) => ({ words: upsert(state.words, word) }));
        trackVocabularyWordLearned({
          surface,
          heard,
          heardRealWord: heardReal,
          newWord,
        });
        void refreshSuggestions();
        return word;
      },

      clear: () => {
        account += 1;
        loadSeq += 1;
        set({ words: [], suggestions: [], loadedAt: null });
      },
    };
  }),
);
